import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Users as UsersIcon,
  MapPinned,
  Image,
  Smartphone,
  ShoppingCart,
  Sparkles,
  Settings as SettingsIcon,
  ScrollText,
  LogOut,
} from 'lucide-react';
import { api, clearTokens } from '@/lib/api';
import { cn } from '@/lib/utils';

type Me = {
  id: string;
  email: string;
  displayName: string;
  role: 'user' | 'admin' | 'super_admin';
};

const NAV: Array<{ to: string; label: string; icon: typeof UsersIcon; group?: string }> = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/users', label: 'Users', icon: UsersIcon },
  { to: '/trips', label: 'Trips', icon: MapPinned },
  { to: '/feed', label: 'Feed', icon: Image },
  { to: '/esim/plans', label: 'eSIM Plans', icon: Smartphone, group: 'eSIM' },
  { to: '/esim/purchases', label: 'Purchases', icon: ShoppingCart },
  { to: '/ai', label: 'AI Conversations', icon: Sparkles },
  { to: '/settings', label: 'Settings', icon: SettingsIcon, group: 'System' },
  { to: '/audit', label: 'Audit Log', icon: ScrollText },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const nav = useNavigate();

  const { data: me } = useQuery<Me>({
    queryKey: ['/admin/api/me'],
    queryFn: () => api.get<Me>('/admin/api/me'),
    staleTime: 10 * 60 * 1000,
  });

  function logout() {
    clearTokens();
    window.dispatchEvent(new Event('taiya:auth-cleared'));
    nav('/');
  }

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className="w-60 bg-surface border-r border-border flex flex-col">
        <div className="px-5 py-6 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-accent" />
            <span className="font-bold tracking-wider">TAIYA</span>
          </div>
          <div className="text-text3 text-xs mt-1">Admin</div>
        </div>

        <nav className="flex-1 py-4 space-y-0.5 overflow-y-auto">
          {NAV.map((item, i) => {
            const Icon = item.icon;
            const active =
              item.to === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.to);
            const prevGroup = i > 0 ? NAV[i - 1].group : undefined;
            return (
              <div key={item.to}>
                {item.group && item.group !== prevGroup && (
                  <div className="px-5 pt-4 pb-1 text-[10px] uppercase tracking-widest text-text3">
                    {item.group}
                  </div>
                )}
                <Link
                  to={item.to}
                  className={cn(
                    'flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-colors',
                    active
                      ? 'bg-surface2 text-text border-accent'
                      : 'text-text2 border-transparent hover:bg-surface2/60 hover:text-text',
                  )}
                >
                  <Icon size={16} strokeWidth={2.2} />
                  {item.label}
                </Link>
              </div>
            );
          })}
        </nav>

        {/* User block */}
        <div className="border-t border-border p-4">
          <div className="text-xs text-text3 uppercase tracking-wider mb-1">
            Signed in as
          </div>
          <div className="text-sm font-medium truncate" title={me?.email}>
            {me?.displayName ?? '…'}
          </div>
          <div className="text-xs text-text2 truncate mb-3" title={me?.email}>
            {me?.email}
          </div>
          <div className="flex items-center justify-between gap-2">
            {me?.role && (
              <span
                className={cn(
                  'ad-badge',
                  me.role === 'super_admin' ? 'ad-badge-accent' : 'ad-badge-neutral',
                )}
              >
                {me.role}
              </span>
            )}
            <button
              onClick={logout}
              className="text-text3 hover:text-error transition-colors"
              title="Sign out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        <div className="px-8 py-7 max-w-[1600px]">{children}</div>
      </main>
    </div>
  );
}
